"use client";

import * as React from "react";
import { cn } from "~/lib/utils";

const TabsContext = React.createContext<{
    value: string;
    onValueChange: (value: string) => void;
} | null>(null);

interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
    defaultValue: string;
    value?: string;
    onValueChange?: (value: string) => void;
}

export function Tabs({
    defaultValue,
    value,
    onValueChange,
    className,
    children,
    ...props
}: TabsProps) {
    const [internalValue, setInternalValue] = React.useState(defaultValue);

    const controlledValue = value ?? internalValue;
    const handleChange = React.useCallback(
        (newValue: string) => {
            if (onValueChange) {
                onValueChange(newValue);
            }
            setInternalValue(newValue);
        },
        [onValueChange]
    );

    return (
        <TabsContext.Provider value={{ value: controlledValue, onValueChange: handleChange }}>
            <div className={cn("w-full", className)} {...props}>
                {children}
            </div>
        </TabsContext.Provider>
    );
}

type TabsListProps = React.HTMLAttributes<HTMLDivElement>;

export function TabsList({ className, children, ...props }: TabsListProps) {
    return (
        <div
            className={cn(
                "inline-flex h-10 items-center justify-start w-full",
                className
            )}
            {...props}
        >
            {children}
        </div>
    );
}

interface TabsTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    value: string;
}

export function TabsTrigger({
    className,
    value,
    children,
    ...props
}: TabsTriggerProps) {
    const context = React.useContext(TabsContext);
    if (!context) throw new Error("TabsTrigger must be used within Tabs");

    const isSelected = context.value === value;

    return (
        <button
            type="button"
            className={cn(
                "inline-flex items-center justify-center whitespace-nowrap px-4 py-3 text-sm font-regular ring-offset-white transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1c2024] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 cursor-pointer",
                "border-b-2 border-transparent text-[#5F646C]",
                isSelected && "border-[#2F4458] text-[#1c3144] font-semibold",
                className
            )}
            onClick={() => context.onValueChange(value)}
            {...props}
        >
            {children}
        </button>
    );
}

interface TabsContentProps extends React.HTMLAttributes<HTMLDivElement> {
    value: string;
}

export function TabsContent({
    className,
    value,
    children,
    ...props
}: TabsContentProps) {
    const context = React.useContext(TabsContext);
    if (!context) throw new Error("TabsContent must be used within Tabs");

    if (context.value !== value) return null;

    return (
        <div
            className={cn(
                "ring-offset-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1c2024] focus-visible:ring-offset-2",
                className
            )}
            {...props}
        >
            {children}
        </div>
    );
}
